import { type FC, useState, useEffect } from 'react';
import { NodeType, NODE_TYPE_COLORS } from '@/types/node.types';

interface AddNodeSheetProps {
  open: boolean;
  onClose: () => void;
  onCreate: (type: NodeType, title: string) => Promise<void>;
  onCreateZone?: () => void;
}

const ITEMS: Array<{ type: NodeType; label: string; icon: string; description: string }> = [
  { type: NodeType.Vision, label: 'Vision', icon: '★', description: 'Mission, valeurs, horizon long-terme' },
  { type: NodeType.Objective, label: 'Objectif', icon: '◎', description: 'OKR, axe stratégique, BSC' },
  { type: NodeType.Activity, label: 'Activité', icon: '⚡', description: 'Projet, action, initiative' },
  { type: NodeType.Risk, label: 'Risque', icon: '⚠', description: 'Menace, danger, point de vigilance' },
  { type: NodeType.Opportunity, label: 'Opportunité', icon: '✨', description: 'Occasion favorable, levier de croissance' },
  { type: NodeType.Resource, label: 'Ressource', icon: '⚙', description: 'RH, matériel, budget, équipe' },
  { type: NodeType.KPI, label: 'KPI', icon: '📊', description: 'Indicateur clé de performance' },
  { type: NodeType.Stakeholder, label: 'Partie prenante', icon: '\u{1F465}', description: 'Acteur impacté (client, partenaire, régulateur, équipe)' },
];

export const AddNodeSheet: FC<AddNodeSheetProps> = ({ open, onClose, onCreate, onCreateZone }) => {
  const [step, setStep] = useState<'type' | 'title'>('type');
  const [selectedType, setSelectedType] = useState<NodeType | null>(null);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep('type');
      setSelectedType(null);
      setTitle('');
      setBusy(false);
    }
  }, [open]);

  if (!open) return null;

  const handlePick = (type: NodeType) => {
    setSelectedType(type);
    setStep('title');
  };

  const handleCreate = async () => {
    if (!selectedType || !title.trim()) return;
    setBusy(true);
    try {
      await onCreate(selectedType, title.trim());
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const color = selectedType ? NODE_TYPE_COLORS[selectedType] : 'var(--color-primary)';

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 900,
        }}
      />
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          maxHeight: '80vh',
          backgroundColor: 'var(--color-surface)',
          borderRadius: '20px 20px 0 0',
          boxShadow: '0 -10px 40px rgba(0,0,0,0.4)',
          zIndex: 910,
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 'env(safe-area-inset-bottom, 0)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px' }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, backgroundColor: 'var(--color-border)' }} />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 16px 16px',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          {step === 'title' && (
            <button
              onClick={() => setStep('type')}
              style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--color-text)', padding: 4, marginRight: 8 }}
              aria-label="Retour"
            >
              ←
            </button>
          )}
          <h2 style={{ flex: 1, fontSize: 18, fontWeight: 700 }}>
            {step === 'type' ? 'Ajouter au canvas' : `Nouveau : ${selectedType}`}
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--color-text-muted)', padding: 4 }}
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {step === 'type' && (
            <>
              {ITEMS.map((item) => (
                <button
                  key={item.type}
                  onClick={() => handlePick(item.type)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    width: '100%',
                    padding: '14px 16px',
                    marginBottom: 8,
                    backgroundColor: 'var(--color-background)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 14,
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      backgroundColor: NODE_TYPE_COLORS[item.type],
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 20,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text)' }}>
                      {item.label}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>
                      {item.description}
                    </div>
                  </div>
                </button>
              ))}
              {onCreateZone && (
                <button
                  onClick={() => {
                    onCreateZone();
                    onClose();
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    width: '100%',
                    padding: '14px 16px',
                    marginTop: 8,
                    backgroundColor: 'transparent',
                    border: '1px dashed var(--color-border)',
                    borderRadius: 14,
                    textAlign: 'left',
                    cursor: 'pointer',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      border: '2px dashed var(--color-border)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 20,
                      flexShrink: 0,
                    }}
                  >
                    ▦
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>Ajouter une zone</div>
                </button>
              )}
            </>
          )}

          {step === 'title' && selectedType && (
            <>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Titre
              </label>
              <input
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreate();
                }}
                placeholder="Ex : Doubler l'ARR d'ici fin 2026"
                style={{
                  width: '100%',
                  marginTop: 8,
                  padding: '14px 16px',
                  fontSize: 16,
                  backgroundColor: 'var(--color-background)',
                  color: 'var(--color-text)',
                  border: `2px solid ${color}`,
                  borderRadius: 14,
                  outline: 'none',
                }}
              />
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--color-text-muted)' }}>
                Tu pourras modifier la description, la priorité et le statut ensuite.
              </div>
            </>
          )}
        </div>

        {step === 'title' && (
          <div
            style={{
              padding: 16,
              borderTop: '1px solid var(--color-border)',
            }}
          >
            <button
              onClick={handleCreate}
              disabled={busy || !title.trim()}
              style={{
                width: '100%',
                padding: 16,
                borderRadius: 14,
                border: 'none',
                backgroundColor: color,
                color: '#fff',
                fontSize: 16,
                fontWeight: 700,
                opacity: busy || !title.trim() ? 0.5 : 1,
              }}
            >
              {busy ? 'Création…' : 'Créer le noeud'}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default AddNodeSheet;
